import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  FileTextOutlined,
  SafetyOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Card, Image, Input, message, Modal, Spin, Upload } from "antd";
import React, { useEffect, useRef, useState } from "react";
import useAxios from "../../../hooks/useAxios";
import CropImage from "../components/CropImage";
import StatusIndicator, {
  VerifiedIndicator,
} from "./components/StatusIndicator";
import StatusMessage from "./components/StatusMessage";
import ValidationPopup from "../components/ValidationPopup";
import { useLocation } from "react-router-dom";
import swal from "sweetalert";
import { capitalizeSentence } from "../utils/commonFunctions";

const MB = 3;

const checkAgeValidation = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age >= 18;
};

const AadharCard = ({
  application,
  getAadharAndPanDetails,
  loadApplication,
  uploadedFile,
  aadharDetails,
  url,
  setDisabledDueToAadhar,
  aiSettings,
}) => {
  const ID = application?._id;
  const [load, setLoad] = useState(false);
  const api = useAxios();
  const [file, setFile] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [modal, setModal] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);
  const [crop, setCrop] = useState();
  const [imgSrc, setImgSrc] = useState("");
  const [errorAadhar, setErrorAadhar] = useState("");
  const [error, setError] = useState("");
  const [aadhar, setAadhar] = useState({
    aadhar_no: application?.aadharNoFilled || "",
    show_aadhar_otp: false,
    aadhar_client_id: "",
    aadhar_otp: "",
  });
  const [openWarningModal, setOpenWarningModal] = useState(false);
  const [aadharAgeError, setAAdharAgeError] = useState(false);
  const [errorVerification, setErrorVerification] = useState({});
  const [response, setResponse] = useState({});

  const toggle = () => setModal(!modal);
  const previewToggle = () => setPreviewModal(!previewModal);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const _status = searchParams.get("status" || "");
  const aadharRef = useRef();

  useEffect(() => {
    if (_status === "success") {
      if (aadharRef.current) {
        aadharRef.current.scrollIntoView({ behavior: "smooth" });
      }
      setTimeout(() => {
        getAadharAndPanDetails();
      }, 6000);
    }
  }, [_status]);

  useEffect(() => {
    if (!application) return;
    setAadhar({
      ...aadhar,
      aadhar_no: application?.aadharNoFilled || "",
    });
  }, [application?.aadharNoFilled]);

  useEffect(() => {
    let disabled = false;

    if (aadharDetails?.documentStatus) return;

    if (aadharDetails?.aadharVerificationDetails?.data?.dob) {
      const dob = aadharDetails?.aadharVerificationDetails?.data?.dob;
      const validation = checkAgeValidation(dob);
      if (!validation) {
        setDisabledDueToAadhar(true);
        setOpenWarningModal(true);
        setAAdharAgeError(true);
        return;
      }
    }

    if (
      aadharDetails?.aadharVerificationDetails &&
      aadharDetails?.aadharVerificationDetails?.status_code === 200 &&
      aadharDetails?.verificationDetails
    ) {
      const { aadharVerificationDetails, verificationDetails } = aadharDetails;

      if (verificationDetails && verificationDetails?.length !== 0) {
        const { digiWithOCR, digiWithProfile, isInfoUpdated } =
          verificationDetails?.[0] || {};

        if ((!digiWithOCR || !digiWithProfile) && !isInfoUpdated) {
          setOpenWarningModal(true);
          setResponse(aadharVerificationDetails);
        }
        disabled = false;
      } else {
        disabled = false;
        if (message === "Service Expired") {
          disabled = false;
          setOpenWarningModal(false);
        } else {
          disabled = true;
        }
      }
    } else {
      if (aadharDetails?.aadharVerificationDetails?.status_code === 502) {
        disabled = false;
      } else {
        disabled = true;
      }
      setOpenWarningModal(false);
    }

    setDisabledDueToAadhar(disabled);
  }, [aadharDetails]);

  const resetCrop = () => {
    setImgSrc("");
    setCrop(undefined);
    setAspectRatio(16 / 9);
    setFile(null);
  };

  function onSelectFile(fileData) {
    const { file, fileList } = fileData;
    if (file && fileList.length > 0) {
      if (file.size > MB * 1024 * 1024) {
        message.warning(`Image size must be less than ${MB}MB`, 2);
        return;
      }
      setCrop(undefined);
      const aspect = 130 / 140;
      setAspectRatio(aspect);
      setFile(file);
      const reader = new FileReader();
      reader.addEventListener(
        "load",
        () => (setImgSrc(reader.result?.toString() || ""), toggle())
      );
      reader.readAsDataURL(file);
    }
  }

  const onClickImgHandler = async (file) => {
    setError("");
    setErrorAadhar("");
    const form = new FormData();
    form.append("aadharCardImage", file);
    setLoad(true);
    try {
      const result = await api.put(`/candidate/aadharCardImage/${ID}`, form, {
        tokenType: "candidate",
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const { response } = result?.data?.OCRDetails;

      const promise = [loadApplication(false, false, "aadhar")];
      promise.push(getAadharAndPanDetails());
      await Promise.all(promise);
      if (aiSettings?.isActive) {
        if ("error" in response) {
          setError(response?.error);
        } else {
          message.success(`Aadhar Card uploaded successfully`, 2);
        }
      } else {
        if ("error" in response) {
          setError(response?.error);
        }
        message.success(`Aadhar Card uploaded successfully`, 2);
      }
      resetCrop();
    } catch (err) {
      console.log(err);
      setError(err?.response?.data?.error?.OCRDetails?.response?.error);
    } finally {
      setLoad(false);
    }
  };

  const onChangeAadharHandler = (e) => {
    let input = e.target.value.replace(/[^0-9]/g, "");
    if (input.length > 12) {
      input = input.slice(0, 12);
    }

    setAadhar({
      ...aadhar,
      aadhar_no: input,
    });
  };

  const verifyThruDigsLocker = async (e) => {
    e.preventDefault();
    setErrorAadhar("");
    if (
      aadhar?.aadhar_no?.replace(/ /g, "") !== "" &&
      aadhar?.aadhar_no?.replace(/ /g, "").length === 12
    ) {
      setLoad(true);
      try {
        const { data } = await api.post(
          `/candidatedocuments//digilocker/initialize`,
          {
            aadharNo: aadhar?.aadhar_no?.replace(/ /g, ""),
            candidateId: ID,
          },
          {
            tokenType: "candidate",
          }
        );

        if (data.response.success) {
          window.location.href = data.response.data.url;
        }
      } catch (err) {
        console.error(err);
        await Promise.all([
          loadApplication(false, false, ""),
          getAadharAndPanDetails(),
        ]);
        message.error(err.response.data.error.message);
      } finally {
        setLoad(false);
      }
    } else {
      setErrorAadhar("Aadhar Number must be 12 digits");
    }
  };

  const updateDetailsHandler = async () => {
    try {
      setLoad(true);
      const { data } = response;
      const { full_name, dob } = data;
      await api.put(
        `/candidate/candidateInfo/${application?._id}`,
        {
          name: full_name,
          dob: dob,
        },
        {
          tokenType: "candidate",
        }
      );
      await Promise.all([
        loadApplication(false, false, "aadhar"),
        getAadharAndPanDetails(),
      ]);
      localStorage.setItem("candidate.name", full_name);
      setOpenWarningModal(false);
      setErrorVerification({});
      message.success("Name updated Successfully", 2);
    } catch (err) {
      console.error(err);
    } finally {
      setLoad(false);
    }
  };

  const reUploadHandler = async () => {
    try {
      setLoad(true);
      await api.put(
        `/candidate/resetAadharDetails/${application?._id}`,
        {},
        {
          tokenType: "candidate",
        }
      );
      await Promise.all([
        getAadharAndPanDetails(),
        loadApplication(false, false, ""),
      ]);
      setOpenWarningModal(false);
      setAAdharAgeError(false);
      setErrorVerification({});
      message.success("Aadhar reset Successfully", 2);
    } catch (err) {
      console.error(err);
    } finally {
      setLoad(false);
    }
  };

  const uploaded = uploadedFile && uploadedFile?.length !== 0;

  return (
    <Spin
      style={{ backgroundColor: "rgba(0, 0, 0, 0.47)" }}
      spinning={load}
      tip={
        <div>
          <p className="text-white font-weight-normal">
            Uploading another file won't affect this one.
          </p>
        </div>
      }
      size="large"
    >
      <Card
        className={`mb-4 bg-white`}
        style={{ border: "1px solid grey", scrollMarginTop: "100px" }}
        title={null}
        ref={aadharRef}
      >
        <div className="d-flex flex-column px-4" style={{ gap: 10 }}>
          <div className="d-flex justify-content-center">
            <StatusIndicator
              filledStatus={application?.aadharCardImageFilledStatus}
              status={application?.aadharStatus}
            />
          </div>
          <div>
            <h5 className="text-dark text-center font-weight-bold mb-1">
              Aadhaar Card
            </h5>
            <StatusMessage
              filledStatus={application?.aadharCardImageFilledStatus}
              status={application?.aadharStatus}
              reason={application?.aadharRejection}
              numberReason={
                aadharDetails?.rejectionReason
                  ? `Aadhar Number verification failed. ${aadharDetails?.rejectionReason}`
                  : undefined
              }
              type={"Aadhar Card"}
            />
          </div>
          <div>
            {uploaded && (
              <hr
                style={{ borderTop: "1px solid #D5D5D5" }}
                className="mb-3"
              ></hr>
            )}
            <div className="d-flex align-items-center flex-column gap-1">
              {uploaded && (
                <Image
                  style={{ objectFit: "contain" }}
                  src={url}
                  width={200}
                  height={100}
                />
              )}
              {!application?.aadharStatus && (
                <Upload
                  listType="picture"
                  maxCount={1}
                  fileList={null}
                  onChange={(value) => onSelectFile(value)}
                  onPreview={(file) => {
                    previewToggle();
                  }}
                  showUploadList={{
                    showRemoveIcon: false,
                  }}
                  //   disabled={}
                  beforeUpload={(file) => {
                    const isValid =
                      file.type === "image/png" ||
                      file.type === "image/jpeg" ||
                      file.type === "image/jpg";

                    if (!isValid) {
                      message.error(
                        "Only PNG, JPG, JPEG and JFIF files are allowed."
                      );
                      return Upload.LIST_IGNORE;
                    }

                    return false;
                  }}
                  accept="image/png, image/jpg, image/jpeg"
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <Button
                      data-testid = "upload-aadhar"
                      style={{ border: "1px dashed #6c757d" }}
                      icon={<UploadOutlined />}
                    >
                      {uploaded ? "Re Upload Aadhar" : "Upload Document"}
                    </Button>
                  </div>
                </Upload>
              )}
              {error && <p className="text-danger">{capitalizeSentence(error || "")}</p>}
            </div>
            {uploaded && (
              <>
                <hr
                  className="my-3"
                  style={{ borderTop: "1px solid #D5D5D5" }}
                ></hr>
                <div className="d-flex align-items-center gap-1">
                  <label
                    className="font-weight-normal mb-0"
                    style={{ fontSize: "1rem" }}
                  >
                    Aadhar&nbsp;Number:&nbsp;
                  </label>
                  <div className="w-100">
                    <Input
                      status={errorAadhar ? "error" : null}
                      value={
                        aadharDetails?.documentStatus === true ||
                        (aadharDetails?.isFilledStatus === true &&
                          aadharDetails?.aadharVerificationDetails
                            ?.status_code === 200)
                          ? "XXXX XXXX " +
                            application?.aadharNoFilled?.slice(-4)
                          : aadhar?.aadhar_no
                      }
                      disabled={
                        aadharDetails?.documentStatus === true ||
                        (aadharDetails?.isFilledStatus === true &&
                          aadharDetails?.aadharVerificationDetails
                            ?.status_code === 200) ||
                        aadhar?.show_aadhar_otp
                      }
                      onChange={onChangeAadharHandler}
                      type="text"
                      placeholder="Enter Aadhar Number"
                    />
                    {errorAadhar !== "" && (
                      <div className="row input-group">
                        <div className="col-12 input-group">
                          <b className="text-danger">{errorAadhar}</b>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {aadharDetails?.documentStatus === true ||
                (aadharDetails?.isFilledStatus === true &&
                  aadharDetails?.aadharVerificationDetails?.status_code ===
                    200) ? (
                  <>
                    <p
                      className="text-center mb-1"
                      style={{
                        fontSize: "16px",
                        color: "#00AC1A",
                        marginTop: 10,
                        display: "flex",
                        gap: 10,
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircleFilled
                        style={{ fontSize: "1.2rem", color: "#00AC1A" }}
                      />
                      Verified through DigiLocker.
                    </p>
                  </>
                ) : (
                  <>
                    <hr
                      className="my-3"
                      style={{ borderTop: "1px solid #D5D5D5" }}
                    ></hr>
                    <div className="d-flex justify-content-center">
                      <button
                        className="btn btn-primary"
                        onClick={verifyThruDigsLocker}
                        style={{
                          // border: "1px solid #777777",
                          // background: "#fff",
                          // outline: "none",
                          // borderRadius: "4px",
                          // padding: "7px 20px",
                          // color: "#777",
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          fontSize: "15px",
                        }}
                      >
                        <SafetyOutlined style={{ fontSize: "18px" }} />
                        {aadharDetails?.aadharVerificationDetails?.message
                          ? "Re "
                          : ""}
                        Verify Through DigiLocker
                      </button>
                    </div>
                    {aadharDetails?.aadharVerificationDetails?.message &&
                      aadharDetails?.documentStatus !== false && (
                        <p
                          className="text-center mb-1"
                          style={{
                            fontSize: "16px",
                            color: "red",
                            marginTop: 10,
                            display: "flex",
                            gap: 10,
                            justifyContent: "center",
                          }}
                        >
                          {aadharDetails?.aadharVerificationDetails?.message}
                        </p>
                      )}
                  </>
                )}
                {application?.aadharStatus && aadharDetails?.documentStatus && (
                  <div className="d-flex justify-content-center mt-3">
                    <VerifiedIndicator />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Card>
      <Modal
        title={`Crop Aadhar Card`}
        centered
        open={modal}
        zIndex={9}
        onOk={() => toggle()}
        closable={false}
        width={700}
        footer={null}
        className="crop-ant-modal-body"
      >
        <CropImage
          key={file}
          setCrop={setCrop}
          crop={crop}
          imgSrc={imgSrc}
          file={file}
          toggle={toggle}
          aspectRatio={aspectRatio}
          setAspectRatio={setAspectRatio}
          onClickImgHandler={onClickImgHandler}
        />
      </Modal>
      {openWarningModal && (
        <ValidationPopup
          aadharAgeError={aadharAgeError}
          application={application}
          details={aadharDetails}
          errorVerification={errorVerification}
          openWarningModal={openWarningModal}
          updateDetailsHandler={updateDetailsHandler}
          reUploadHandler={reUploadHandler}
          reUploadHandlerPan={undefined}
          setOpenWarningModal={setOpenWarningModal}
          fileType={"aadhar"}
        />
      )}
    </Spin>
  );
};

export default React.memo(AadharCard, (prev, next) => {
  return (
    prev?.aadharDetails === next?.aadharDetails &&
    prev?.application?.aadharCardImage === next?.application?.aadharCardImage &&
    prev?.application?.aadharNoFilled === next?.application?.aadharNoFilled &&
    prev?.application?.aadharCardNo === next?.application?.aadharCardNo &&
    prev?.application?.IsSelected === next?.application?.IsSelected &&
    prev?.errorAadhar === next?.errorAadhar &&
    prev?.url === next?.url &&
    JSON.stringify(prev?.aadharDetails) ===
      JSON.stringify(next?.aadharDetails) &&
    JSON.stringify(prev?.application?.aadharCardImage) ===
      JSON.stringify(next?.application?.aadharCardImage)
  );
});
